import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task, type Task } from 'ember-concurrency';
import link from 'ember-link/helpers/link';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { eq, not, or } from 'ember-truth-helpers';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import FeedItemFlaggedScreener from 'garaje/components/approvals/feed-item-flagged-screener';
import NoReviewPermissionTooltip from 'garaje/components/approvals/no-review-permission-tooltip';
import WorkflowDecisionModal from 'garaje/components/approvals/workflow-decision-modal';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import UiPill from 'garaje/components/ui-pill';
import VisitorProfileHoverCard from 'garaje/components/visitors/profiles/hover-card';
import arrayToSentence from 'garaje/helpers/array-to-sentence';
import removedString from 'garaje/helpers/removed-string';
import type { Report, ReportSource } from 'garaje/models/approval-status';
import type ApprovalStatusModel from 'garaje/models/approval-status';
import ConnectInvite from 'garaje/models/connect-invite';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import type ScreenerResult from 'garaje/models/screener-result';
import type { BlocklistReport } from 'garaje/models/screener-result';
import type User from 'garaje/models/user';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type AjaxService from 'garaje/services/ajax';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import type { WorkflowTasksPermissionsResponse } from 'garaje/types/workflow-approvals';
import {
  deriveCanApproveWorkflow,
  deriveUserIsAssignedApproverWithoutPermission,
} from 'garaje/utils/derive-can-approve-workflow';
import { BlocklistFilterSource } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { getScreenerDisplayName } from 'garaje/utils/get-screener-display-name';
import { notifyWorkflow } from 'garaje/utils/notify-workflow';
import { shouldShowVisitorProfileHoverCard } from 'garaje/utils/visitor-profile-hover-card';
import { reads } from 'macro-decorators';

const APPROVAL_REQUIRED_COPY = {
  location: 'This invite requires approval by a property admin.',
  property: 'This invite requires approval by a tenant admin.',
};

type Context = 'location' | 'property';

interface InviteFeedItemFlaggedSignature {
  Args: {
    /**
     * ember-concurrency task to be performed when approving an invite. Receives the invite as its argument.
     */
    approveInviteTask: Task<void, [InviteWithVisitApproval, screeners?: ReportSource[]]>;
    /**
     * List of users who are set as contacts for blocklist feature, so their names can be displayed.
     */
    blocklistContacts: User[];
    /**
     * The context in which the component is being used ("location" or "property"). Defaults to "location".
     */
    context?: Context;
    /**
     * ember-concurrency task to be performed when denying an invite. Receives the invite as its argument.
     */
    denyInviteTask: Task<void, [InviteWithVisitApproval]>;
    /**
     * List of fields which are being shown. This is used to calculate the correct `colspan` to match the
     * width of non-flagged invite rows in a table.
     */
    fields: Array<{ show: boolean }>;
    invite: InviteWithVisitApproval;
    /**
     * Optional callback to mark invite as reviewed after workflow approval/denial
     */
    onInviteReviewed?: (invite: InviteWithVisitApproval) => void;
  };
  Element: HTMLDivElement;
}

export default class InviteFeedItemFlagged extends Component<InviteFeedItemFlaggedSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajax: AjaxService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @reads('state.features.canAccessMultiPartApprovals') canAccessMultiPartApprovals!: boolean;

  @tracked workflowPermissionsChecked = false;
  @tracked canApproveWorkflow = true;
  @tracked userIsAssignedApproverWithoutPermission = false;
  @tracked showWorkflowDecisionModal = false;
  @tracked workflowDecisionAction: 'approve' | 'deny' | null = null;
  @tracked workflowDecisionReason?: string;

  constructor(owner: unknown, args: InviteFeedItemFlaggedSignature['Args']) {
    super(owner, args);

    if (this.featureFlags.isEnabled('ava-feature') && this.workflowId) {
      void this.fetchWorkflowPermissionsTask.perform();
    } else {
      this.workflowPermissionsChecked = true;
    }
  }

  get workflowId(): string | null {
    if (!this.featureFlags.isEnabled('ava-feature')) {
      return null;
    }

    if (this.isConnectInvite(this.args.invite)) {
      return null;
    }

    const resultWithWorkflow = this.args.invite.visitApproval.screenerResults.find(
      (result) => result.workflowId !== null,
    );
    return resultWithWorkflow?.workflowId ?? null;
  }

  get isPartiallyApproving(): boolean {
    if (this.isConnectInvite(this.args.invite)) return false;
    return this.args.invite.visitApproval.partiallyApprovable;
  }

  get unreviewableScreenerSources(): string {
    if (this.isConnectInvite(this.args.invite)) return '';

    return arrayToSentence(
      this.args.invite.visitApproval.unreviewableScreenerSources.map(
        (source) => getScreenerDisplayName(source) ?? source,
      ),
    );
  }

  get approveCopy(): string {
    const APPROVE_TEXT = 'Approve';

    if (this.isConnectInvite(this.args.invite)) return APPROVE_TEXT;
    // has multiple approvals and can review all
    if (this.multipleApprovals && this.canReviewAllReviewableScreeners) return 'Approve all';
    return APPROVE_TEXT;
  }

  get multipleApprovals(): boolean {
    if (this.isConnectInvite(this.args.invite)) return false;
    if (this.args.invite.visitApproval.reviewResults.length < 2) return false;

    return true;
  }

  get blocklistApproval(): ScreenerResult<BlocklistReport> | ApprovalStatusModel | undefined {
    if (!this.isConnectInvite(this.args.invite)) {
      return this.args.invite.visitApproval.blocklistReviewResult;
    } else {
      if (!this.args.invite.approvalStatus?.didFailBlocklistCheck) return undefined;
      return this.args.invite.approvalStatus;
    }
  }

  get canReview(): boolean {
    if (this.isConnectInvite(this.args.invite)) return this.canReviewAllScreeners;

    // if we are using multi part approvals, we should still be able to review the parts of the approval that we have access to
    if (this.canAccessMultiPartApprovals) return this.canReviewSomeReviewableScreeners;
    return this.canReviewAllReviewableScreeners;
  }

  get canReviewOrReadMatch(): boolean {
    if (this.isConnectInvite(this.args.invite)) {
      return this.abilities.can('review or read blocklist match in security', this.#permissionsModel);
    }

    // if we are using multi part approvals, we should still be able to review or read parts of the approval that we have access to
    if (this.canAccessMultiPartApprovals) {
      return this.canReviewSomeScreeners || this.canViewSomeScreeners;
    } else {
      return this.canReviewAllReviewableScreeners || this.canViewAllScreeners;
    }
  }

  /**
   * user can review at least one screener that is in a review state
   */
  get canReviewSomeReviewableScreeners(): boolean {
    return this.abilities.can('review some reviewable entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review at least one screener regardless of the review status
   */
  get canReviewSomeScreeners(): boolean {
    return this.abilities.can('review some entry-approvals', this.#permissionsModel);
  }

  /**
   * user can view at least one screener regardless of the review status
   */
  get canViewSomeScreeners(): boolean {
    return this.abilities.can('view some entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review all screeners in the approval
   * */
  get canReviewAllScreeners(): boolean {
    return this.abilities.can('review entry-approvals', this.#permissionsModel);
  }

  /**
   * user can review all screeners in the approval of the review status
   * */
  get canReviewAllReviewableScreeners(): boolean {
    return this.abilities.can('review all reviewable entry-approvals', this.#permissionsModel);
  }

  /**
   * user can view all screeners in the approval
   * */
  get canViewAllScreeners(): boolean {
    return this.abilities.can('view entry-approvals', this.#permissionsModel);
  }

  get context(): Context {
    return this.args.context ?? 'location';
  }

  get report(): Report[] {
    if (this.isConnectInvite(this.args.invite)) {
      return this.args.invite.approvalStatus?.failedReport ?? [];
    }
    return [];
  }

  get hasPropertyReport(): boolean {
    return !this.isConnectInvite(this.args.invite)
      ? this.args.invite.visitApproval.reviewResults.some((result) => result.isPropertyBlocklist)
      : this.report.some((report) => report.blacklistFilterSource === BlocklistFilterSource.PROPERTY);
  }

  get showDetails(): boolean {
    return this.context === 'location';
  }

  // Show an "Edited" pill when the invite was edited in such a way as to trigger rescreening.
  // This can be done by, for example, changing the Visitor Type. This status is communicated from
  // the backend by the `employeeTriggeredRescreen` property of the invite's `approvalStatus` or `visitApproval`.
  // For invites with an advanced approval workflow, we also show "Edited" when a visitor changes
  // data, indicated by the `visitorTriggeredRescreen` property.
  get showEditedIndication(): boolean {
    if (this.isConnectInvite(this.args.invite)) {
      return Boolean(this.args.invite.approvalStatus?.employeeTriggeredRescreen);
    }

    const { visitApproval } = this.args.invite;
    return (
      Boolean(visitApproval.employeeTriggeredRescreen) ||
      (Boolean(this.workflowId) && Boolean(visitApproval.visitorTriggeredRescreen))
    );
  }

  get additionalApprovalRequiredCopy(): string {
    // Default copy
    let copy = this.args.invite.preregistrationComplete
      ? 'This invite requires approval.'
      : 'This invite requires approval to send.';

    // If there is a failed property specific report then we need to specify whose approval is required.
    if (this.hasPropertyReport) {
      copy = APPROVAL_REQUIRED_COPY[this.context];
    }

    return copy;
  }

  get colspan(): number {
    // colspan is equal the the number of shown fields
    // only because we have to add 1 for the kebab menu cell,
    // but subtract 1 for the name field, which is already in the row and is not included in the colspan
    return (this.args.fields ?? []).filter((field) => field.show).length;
  }

  get contactedNames(): string[] {
    if (this.blocklistApproval && this.args.blocklistContacts) {
      return this.args.blocklistContacts.map((contact) => contact.fullName);
    }
    return [];
  }

  get connectInvite(): ConnectInvite | undefined {
    if (this.isConnectInvite(this.args.invite)) {
      return this.args.invite;
    }
    return undefined;
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: this.context,
      didFailBlocklistCheck: !this.isConnectInvite(this.args.invite)
        ? !!this.args.invite.visitApproval.blocklistReviewResult
        : !!this.args.invite.approvalStatus?.didFailBlocklistCheck,
      report: this.report,
      visitApproval: !this.isConnectInvite(this.args.invite) ? this.args.invite.visitApproval : undefined,
      parent: this.args.invite,
    };
  }

  isConnectInvite(invite: ConnectInvite | InviteWithVisitApproval): invite is ConnectInvite {
    return invite instanceof ConnectInvite;
  }

  get visitorProfile(): VisitorProfileModel | null {
    if (this.isConnectInvite(this.args.invite)) return null;
    // Defensive .value?.() — see invite-feed-item.gts:156 for rationale + autotracking caveat.
    return (this.args.invite.belongsTo?.('visitorProfile')?.value?.() ?? null) as VisitorProfileModel | null;
  }

  get showHoverCard(): boolean {
    return shouldShowVisitorProfileHoverCard({
      visitorProfile: this.visitorProfile,
      recordId: this.args.invite?.id,
      featureFlags: this.featureFlags,
      abilities: this.abilities,
    });
  }

  get inviteId(): string {
    return this.args.invite?.id ?? '';
  }

  fetchWorkflowPermissionsTask = task(async (): Promise<void> => {
    if (!this.workflowId) {
      this.workflowPermissionsChecked = true;
      return;
    }

    try {
      const response = await this.ajax.request<WorkflowTasksPermissionsResponse>(
        `/a/visits/api/v1/workflows/${this.workflowId}/tasks/permissions`,
      );

      this.canApproveWorkflow = deriveCanApproveWorkflow(response);
      this.userIsAssignedApproverWithoutPermission = deriveUserIsAssignedApproverWithoutPermission(response);
    } catch (_error) {
      // Any error (404, 500, etc.) should disable the buttons
      this.canApproveWorkflow = false;
      this.userIsAssignedApproverWithoutPermission = false;
    } finally {
      this.workflowPermissionsChecked = true;
    }
  });

  approveInviteTask = task(
    waitFor(async (screeners?: ReportSource[], reason?: string) => {
      // This component-local version of the task exists so we can get instance-specific state.
      // The actual logic lives at a higher level so that, when invoked with `.unlinked()`, it all
      // executes; otherwise, once the approved invite was reloaded (either with an `invite.reload()`
      // call or by the realtime subscription that gets notified of changes to invites and pushes them
      // into the store out-of-band), this component gets torn down and the task is aborted before,
      // e.g., the flash message is shown. By keeping that logic out of this component, we don't
      // have to worry about that.
      // The instance-local state means that we can disable this invite's Approve/Deny buttons when
      // approveInviteTask or denyInviteTask is running without having to track state ourselves at
      // the higher level, or ended up disabling the buttons for _all_ invites instead of this one.

      if (this.workflowId) {
        await notifyWorkflow(this.ajax, this.flashMessages, this.workflowId, true, reason);

        const nonWorkflowScreeners = this.args.invite.visitApproval?.reviewableScreenerSourcesWithoutWorkflow ?? [];
        if (nonWorkflowScreeners.length) {
          // Bypass the parent task: its flash uses approvalMessage, which is misleading here
          // because the workflow service's approval propagates async and the workflow screener
          // still appears in review at the moment the review response is generated.
          try {
            await this.args.invite.approve(nonWorkflowScreeners);
          } catch (error) {
            this.flashMessages.showAndHideFlash('error', 'Error approving invite', {
              details: parseErrorForDisplay(error),
            });
            return;
          }
        }

        this.flashMessages.showAndHideFlash('success', 'Visitor approved');
        this.metrics.trackEvent('Dashboard Invite - Reviewed', {
          action: 'approve',
          invite_id: this.args.invite.id,
          source: 'Feed',
          workflow: true,
        });
        // Mark invite as reviewed to remove it from the list
        if (this.args.onInviteReviewed) {
          this.args.onInviteReviewed(this.args.invite);
        }
        return;
      }

      await this.args.approveInviteTask.unlinked().perform(this.args.invite, screeners);
    }),
  );

  denyInviteTask = task(
    waitFor(async (reason?: string) => {
      // This component-local version of the task exists so we can get instance-specific state.
      // The actual logic lives at a higher level so that, when invoked with `.unlinked()`, it all
      // executes; otherwise, once the denied invite was reloaded (either with an `invite.reload()`
      // call or by the realtime subscription that gets notified of changes to invites and pushes them
      // into the store out-of-band), this component gets torn down and the task is aborted before,
      // e.g., the flash message is shown. By keeping that logic out of this component, we don't
      // have to worry about that.
      // The instance-local state means that we can disable this invite's Approve/Deny buttons when
      // approveInviteTask or denyInviteTask is running without having to track state ourselves at
      // the higher level, or ended up disabling the buttons for _all_ invites instead of this one.

      if (this.workflowId) {
        await notifyWorkflow(this.ajax, this.flashMessages, this.workflowId, false, reason);

        const nonWorkflowScreeners = this.args.invite.visitApproval?.reviewableScreenerSourcesWithoutWorkflow ?? [];
        if (nonWorkflowScreeners.length) {
          try {
            await this.args.invite.deny(nonWorkflowScreeners);
          } catch (error) {
            this.flashMessages.showAndHideFlash('error', 'Error denying invite', {
              details: parseErrorForDisplay(error),
            });
            return;
          }
        }

        this.flashMessages.showAndHideFlash('success', 'Visitor denied');
        this.metrics.trackEvent('Dashboard Invite - Reviewed', {
          action: 'deny',
          invite_id: this.args.invite.id,
          source: 'Feed',
          workflow: true,
        });
        // Mark invite as reviewed to remove it from the list
        if (this.args.onInviteReviewed) {
          this.args.onInviteReviewed(this.args.invite);
        }
        return;
      }

      await this.args.denyInviteTask.unlinked().perform(this.args.invite);
    }),
  );

  @action
  trackClickInviteDetail(invite_id: string): void {
    this.metrics.trackEvent('Invite Details Clicked', { invite_id });
  }

  @action
  openWorkflowDecisionModal(action: 'approve' | 'deny'): void {
    if (this.workflowId) {
      this.workflowDecisionAction = action;
      this.showWorkflowDecisionModal = true;
    }
  }

  @action
  closeWorkflowDecisionModal(): void {
    this.showWorkflowDecisionModal = false;
    this.workflowDecisionAction = null;
    this.workflowDecisionReason = undefined;
  }

  @action
  handleWorkflowDecisionConfirm(reason?: string): void {
    this.workflowDecisionReason = reason;

    if (this.workflowDecisionAction === 'approve') {
      void this.approveInviteTask.perform(undefined, reason);
    } else if (this.workflowDecisionAction === 'deny') {
      void this.denyInviteTask.perform(reason);
    }
    this.closeWorkflowDecisionModal();
  }

  @action
  handleApproveClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('approve');
    } else {
      void this.approveInviteTask.perform(undefined);
    }
  }

  @action
  handleDenyClick(): void {
    if (this.workflowId) {
      this.openWorkflowDecisionModal('deny');
    } else {
      void this.denyInviteTask.perform(undefined);
    }
  }
  <template>
    <div
      data-test-invite-feed-item-flagged={{@invite.id}}
      class="table-row {{if this.canReviewOrReadMatch 'bg-white' 'text-carbon-70 bg-carbon-5'}}"
      ...attributes
    >
      <div class="table-cell border-b"></div>
      <div class="table-cell border-b align-middle pl-1" style="width: 22px">
        {{#if this.canReviewOrReadMatch}}
          <img
            src="/assets/images/caution-triangle.svg"
            alt="needs attention"
            class="align-middle relative"
            style="min-width: 1.25rem; width:1.25rem; height:1.25rem; right: -1.5em;"
          />
        {{/if}}
      </div>

      <MonogramOrPhoto
        @href={{if this.showDetails (link route="visitors.invites.show" model=@invite.id)}}
        @name={{removedString @invite.fullName}}
        @devicePhoto={{or @invite.photoUrl @invite.entry.content.thumbnails.small}}
        @passportPhoto={{@invite.entry.content.passportAvatar}}
      />

      <div class="table-cell border-b align-middle p-2 text-carbon-50 min-w-[150px]" data-test-invite-feed-item>
        {{#if this.showDetails}}
          {{#if this.showHoverCard}}
            {{! showHoverCard guards visitorProfile non-null }}
            <VisitorProfileHoverCard
              @visitorProfile={{this.visitorProfile}}
              @from="invites"
              @id={{this.inviteId}}
              @fallbackName={{removedString @invite.fullName}}
              @visitRoute="visitors.invites.show"
            >
              <LinkTo
                @route="visitors.invites.show"
                @model={{@invite.id}}
                {{on "click" (fn this.trackClickInviteDetail @invite.id)}}
                class="text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
                data-test-invite-full-name
                data-filter
              >
                {{removedString @invite.fullName}}
              </LinkTo>
            </VisitorProfileHoverCard>
          {{else}}
            <LinkTo
              @route="visitors.invites.show"
              @model={{@invite.id}}
              {{on "click" (fn this.trackClickInviteDetail @invite.id)}}
              class="text-base font-bold no-underline text-carbon-70 hover:text-red-50 trans-color"
              data-test-invite-full-name
              data-filter
            >
              {{removedString @invite.fullName}}
            </LinkTo>
          {{/if}}
        {{else}}
          <div class="text-base font-bold no-underline text-carbon-70" data-test-invite-full-name data-test-filter>
            {{removedString @invite.fullName}}
          </div>
        {{/if}}

        {{#if this.showEditedIndication}}
          <UiPill @fill="newLightBlue" data-test-edited-flag>
            Edited
          </UiPill>
        {{/if}}

        {{#if @invite.host}}
          <p data-test-invite-host-wrapper class="m-0">
            <strong>Host:</strong>
            <span data-test-invite-host>{{removedString @invite.host}}</span>
          </p>
        {{/if}}
      </div>

      {{#if this.canReviewOrReadMatch}}
        <td class="table-cell border-b p-2 align-middle" colspan={{this.colspan}}>
          <div class="flex justify-between align-middle text-carbon-70 pr-8 text-sm">
            <div style="max-width: 30rem">
              <div class="mt-2 text-carbon-50" data-test-log-approval-review-reports>

                <FeedItemFlaggedScreener
                  @multipleApprovals={{this.multipleApprovals}}
                  @model={{@invite}}
                  @visitApproval={{@invite.visitApproval}}
                  @approvalStatus={{this.connectInvite.approvalStatus}}
                  @canReview={{this.canReview}}
                  @canReviewOrRead={{this.canReviewOrReadMatch}}
                  @approveTask={{this.approveInviteTask}}
                  @denyTask={{this.denyInviteTask}}
                />
              </div>
              {{#if this.showDetails}}
                <LinkTo
                  @route="visitors.invites.show"
                  @model={{@invite.id}}
                  {{on "click" (fn this.trackClickInviteDetail @invite.id)}}
                  class="block my-2 text-xs"
                  data-test-log-view-details
                >
                  View details →
                </LinkTo>
              {{/if}}
            </div>
            {{#if this.canReview}}
              <div class="flex justify-center flex-col pl-4">
                <div style="width: 20rem">
                  <button
                    data-test-approve-button
                    type="button"
                    class="btn btn--outline btn--small mr-4"
                    style="width: 8rem"
                    {{on "click" this.handleApproveClick}}
                    disabled={{or
                      this.approveInviteTask.isRunning
                      this.denyInviteTask.isRunning
                      (not this.workflowPermissionsChecked)
                      (not this.canApproveWorkflow)
                    }}
                  >
                    <span class="icon-check-green mr-1"></span>
                    {{this.approveCopy}}
                    {{#if this.isPartiallyApproving}}
                      {{! prettier-ignore }}
                      <EmberTooltip
                        @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white text-shadow-none z-50 !max-w-xs [&_.ember-tooltip-arrow]:hidden"
                      >
                        Approving this invite will still require additional approvals for <strong>{{this.unreviewableScreenerSources}}</strong> from another admin
                      </EmberTooltip>
                    {{/if}}
                  </button>
                  <button
                    data-test-deny-button
                    type="button"
                    class="btn btn--outline btn--small ml-2"
                    style="width: 8rem"
                    {{on "click" this.handleDenyClick}}
                    disabled={{or
                      this.approveInviteTask.isRunning
                      this.denyInviteTask.isRunning
                      (not this.workflowPermissionsChecked)
                      (not this.canApproveWorkflow)
                    }}
                  >
                    <span class="icon-cross-red mr-1"></span>
                    {{if this.multipleApprovals "Deny all" "Deny"}}
                  </button>
                  {{#if this.userIsAssignedApproverWithoutPermission}}
                    <NoReviewPermissionTooltip />
                  {{/if}}
                </div>
              </div>
            {{/if}}
          </div>
        </td>
      {{else}}
        <td class="px-2 border-b align-middle" colspan={{this.colspan}} data-test-security-pending>
          <div style="max-width: 500px;">
            {{this.additionalApprovalRequiredCopy}}
            {{#if this.contactedNames}}
              <br />
              {{arrayToSentence this.contactedNames}}
              {{if (eq this.contactedNames.length 1) "has" "have"}}
              been notified.
            {{/if}}
          </div>
        </td>
      {{/if}}
    </div>

    {{#if this.showWorkflowDecisionModal}}
      <WorkflowDecisionModal
        @isApproving={{eq this.workflowDecisionAction "approve"}}
        @onClose={{this.closeWorkflowDecisionModal}}
        @onConfirm={{this.handleWorkflowDecisionConfirm}}
      />
    {{/if}}
  </template>
}
